import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, CheckCircle2, Users, Award, ShoppingBag, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const roleIcons = { golfer: Users, pro: Award, brand: ShoppingBag, organization: Building2 };
const roleLabels = { golfer: 'Golfer', pro: 'Golf Professional', brand: 'Brand / Business', organization: 'Golf Organization / Club' };

const WaitlistPage = () => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', role: '', zip_code: '',
    beta_tester: false, bunker_question: '', pilot_question: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.role || !form.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!/^[0-9]{5}$/.test(form.zip_code)) {
      toast.error('Zip code must be exactly 5 digits');
      return;
    }
    if (!form.email.includes('@') || !form.email.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/waitlist/join`, form);
      setSubmitted(true);
      toast.success('You\'re on the list!');
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('This email is already on the waitlist!');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-deep-green pt-20 flex items-center justify-center">
        <div className="max-w-md text-center px-6">
          <CheckCircle2 className="w-20 h-20 text-gold mx-auto mb-6" />
          <h1 className="font-display font-bold text-3xl text-white mb-3">You're In!</h1>
          <p className="text-cream/80 font-body mb-2">Welcome to the Fairway Alliance waitlist.</p>
          {form.beta_tester && (
            <p className="text-gold font-semibold text-sm mb-4">You've been flagged for Beta Tester consideration.</p>
          )}
          <p className="text-cream/60 text-sm">We'll reach out when it's your turn to tee off.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-green pt-20 pb-20">
      <div className="container-padding max-w-2xl mx-auto">
        <div className="text-center mb-10 pt-8">
          <img src="/logo.png" alt="Fairway Alliance" className="h-20 w-auto mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="waitlist-title">
            Join the Alliance
          </h1>
          <p className="text-cream/80 font-body text-lg">Connect. Play. Discover. Grow.</p>
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-cream/90">First Name *</Label>
                  <Input value={form.first_name} onChange={e => handleChange('first_name', e.target.value)}
                    placeholder="First" maxLength={50} className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="waitlist-first-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-cream/90">Last Name *</Label>
                  <Input value={form.last_name} onChange={e => handleChange('last_name', e.target.value)}
                    placeholder="Last" maxLength={50} className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="waitlist-last-name" />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-cream/90">Email Address *</Label>
                <Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)}
                  placeholder="you@example.com" className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  data-testid="waitlist-email" />
              </div>

              {/* Joining As */}
              <div className="space-y-2">
                <Label className="text-cream/90">Joining As... *</Label>
                <Select value={form.role} onValueChange={v => handleChange('role', v)}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="waitlist-role">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="golfer">Golfer</SelectItem>
                    <SelectItem value="pro">Golf Professional</SelectItem>
                    <SelectItem value="brand">Brand / Business</SelectItem>
                    <SelectItem value="organization">Golf Organization / Club</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Zip Code */}
              <div className="space-y-2">
                <Label className="text-cream/90">Zip Code *</Label>
                <Input value={form.zip_code} onChange={e => handleChange('zip_code', e.target.value.replace(/\D/g,'').slice(0,5))}
                  placeholder="12345" maxLength={5} className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  data-testid="waitlist-zip" />
              </div>

              {/* Role-specific questions */}
              {form.role === 'pro' && (
                <div className="space-y-2">
                  <Label className="text-cream/90">What is the #1 thing that slows down your business backend?</Label>
                  <Textarea value={form.bunker_question} onChange={e => handleChange('bunker_question', e.target.value)}
                    placeholder="Tell us about your biggest pain point..." rows={3}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="waitlist-bunker" />
                </div>
              )}
              {form.role === 'brand' && (
                <div className="space-y-2">
                  <Label className="text-cream/90">What is your biggest challenge in reaching minority golf audiences today?</Label>
                  <Textarea value={form.pilot_question} onChange={e => handleChange('pilot_question', e.target.value)}
                    placeholder="Tell us about your biggest challenge..." rows={3}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="waitlist-pilot" />
                </div>
              )}

              {/* Beta Tester */}
              <div className="flex items-center gap-3 p-3 bg-gold/10 rounded-sm border border-gold/20">
                <Checkbox id="beta" checked={form.beta_tester}
                  onCheckedChange={v => handleChange('beta_tester', v)}
                  data-testid="waitlist-beta" />
                <Label htmlFor="beta" className="text-cream/90 text-sm cursor-pointer">
                  I want to be one of the Beta Testers
                </Label>
              </div>

              <Button type="submit" className="w-full btn-gold py-6" disabled={submitting} data-testid="waitlist-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Join the Waitlist'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WaitlistPage;
